import { useContext } from 'react';
import { useContextSelector } from 'use-context-selector';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { queryClient } from 'config/queryClient';
import { logger } from 'config/logger';
import { useGTM } from 'hooks/gtm';
import { useDeleteJobImprovedMutation } from 'hooks/shared/useDeleteJobImprovedMutation';
import { EventBus } from 'services/event-bus/EventBus';
import { EnrichVacancyEvent } from 'services/event-bus/EventBus.types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCreateSnackbar } from 'shared/legacy/contexts/SnackbarContext/useCreateSnackbar';
import { QueryKey } from 'types/query';
import { useCreateEnrichedVacancy } from '../../hooks/useCreateEnrichedVacancy';
import { EnrichTab } from '../EditEnrichedVacancyForm/EditEnrichedVacancy.types';
import { EnrichVacancyModalContext } from '../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalState } from '../EnrichVacancyModalContext/EnrichVacancyModalContext.types';

export const useVacancyActions = () => {
  const { dictionary } = useContext(LocalizationContext);
  const { mutateAsync: deleteJobImproved } = useDeleteJobImprovedMutation();

  const gtm = useGTM();
  const selectedVacancy = useContextSelector(EnrichVacancyModalContext, (ctx) => ctx.vacancy);
  const setVacancy = useContextSelector(EnrichVacancyModalContext, (ctx) => ctx.setVacancy);
  const setState = useContextSelector(EnrichVacancyModalContext, (ctx) => ctx.setState);
  const setEditVacancyTab = useContextSelector(EnrichVacancyModalContext, (ctx) => ctx.setEditVacancyTab);
  const createSnackbar = useCreateSnackbar();
  const { handleCreateFromTemplate } = useCreateEnrichedVacancy();

  const handleCreateJob = () => {
    setVacancy(null);
    setState(EnrichVacancyModalState.ChoosingCreationMethod);
    setEditVacancyTab(EnrichTab.EnrichedVersion);
  };

  const handleSelectVacancy = (vacancy: Vacancy) => {
    if (vacancy.is_improved) {
      setVacancy(vacancy);
      setState(EnrichVacancyModalState.EditingEnrichedVacancy);
      setEditVacancyTab(EnrichTab.EnrichedVersion);
      queryClient.invalidateQueries([QueryKey.useEnrichedVacanciesQuery, { search: '' }], { exact: false });

      return;
    }

    handleCreateFromTemplate(vacancy);
  };

  const handleDeleteVacancy = async (vacancy: Vacancy) => {
    try {
      await deleteJobImproved(vacancy);

      setVacancy(null);
      setState(EnrichVacancyModalState.ChoosingCreationMethod);
      setEditVacancyTab(EnrichTab.EnrichedVersion);

      EventBus.emit(EnrichVacancyEvent.Deleted, { vacancyId: Number(vacancy.db_id) });
    } catch (error) {
      logger.error(error);

      createSnackbar((error instanceof Error && error?.message) || dictionary.somethingWentWrong, {
        variant: 'danger',
      });
    }
  };

  const handleEditVacancy = (vacancy: Vacancy) => {
    if (vacancy.db_id === selectedVacancy?.db_id) return;

    if (vacancy.is_improved) {
      setVacancy(vacancy);
      setState(EnrichVacancyModalState.EditingEnrichedVacancy);
      setEditVacancyTab(EnrichTab.EnrichedVersion);
    } else {
      gtm.clickOnEnrichedJob();

      handleCreateFromTemplate(vacancy);
      setState(EnrichVacancyModalState.EnrichingVacancy);
      setEditVacancyTab(EnrichTab.EnrichedVersion);
    }
  };

  return { selectedVacancy, handleCreateJob, handleSelectVacancy, handleDeleteVacancy, handleEditVacancy };
};
